// Shared pieces of the screening panels (SER-5741). The standalone
// contact_screening panel and the outbound_screening composite edit the same
// screening half, and the Call Start Behavior widget is also the ai_agent_v2
// outbound widget — single source of truth for all three consumers.

import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@neo/primitives/collapsible'
import { Input } from '@neo/primitives/input'
import { Select } from '@neo/primitives/select'
import { Typography } from '@neo/primitives/typography'
import { type Node, useUpdateNodeInternals } from '@xyflow/react'
import { ChevronDown } from 'lucide-react'
import type { ChangeEvent } from 'react'
import { useMemo } from 'react'
import { v4 } from 'uuid'
import type { ZodSchema } from 'zod'
import { NodevarList } from '../components/nodevar-list'
import { SCREENING_VOICEMAIL_PORT } from '../config/contact-screening.config'
import { DEFAULT_INITIAL_WAIT_TIME } from '../constants'
import { useFlowStore } from '../store'
import { FlowNodeTypes, type RFInstance } from '../types'
import { getDefaultNodeData } from '../utils'

// Every consumer's FormValues structurally carries these keys, but TS cannot
// prove membership in DeepKeys<T> for an unbound T — each factory carries one
// localized cast instead of duplicating the field blocks per panel.
export interface ScreeningSharedValues {
  context: string
  ask_permission: boolean
  initial_wait_time?: number
  voicemail_detection_next_node?: string
}

type FieldOf<T> = GenericFieldConfig<T>[number]
type SharedForm = GenericFormType<ScreeningSharedValues>

/** "Call Start Behavior" — agent/user-speaks-first select with a conditional
 * seconds input. `extras` lets ai_agent_v2 add its channel gating;
 * `collapsible` renders the card collapsed to a summary row by default (the
 * screening panels — sensible default, config one click away). */
export const callStartBehaviorField = <T,>(
  zodValidation: ZodSchema,
  extras?: { visible?: (state: T) => boolean; onChangeListenTo?: string[]; collapsible?: boolean },
): FieldOf<T> => {
  const { collapsible, ...fieldExtras } = extras ?? {}
  return {
    ...fieldExtras,
    name: 'initial_wait_time',
    type: 'custom',
    label: '',
    component: ({
      value,
      onChange,
      form,
      ...props
    }: {
      value: unknown
      onChange: (e: ChangeEvent<HTMLInputElement>) => void
      form: SharedForm
    }) => {
      const waitTimeValue = typeof value === 'number' ? value : ''
      const isEnabled = value === '' || Number(value) > 0
      const selectedValue = isEnabled ? 'wait-for-user' : 'speak-immediately'

      const handleSelectChange = (val: string) => {
        if (val === 'speak-immediately') {
          form.setFieldValue('initial_wait_time', 0)
        } else {
          // do not reset wait time if it is already set else set default value
          const current = form.getFieldValue('initial_wait_time')
          const wait_time = current && current > 0 ? current : DEFAULT_INITIAL_WAIT_TIME
          form.setFieldValue('initial_wait_time', wait_time)
        }
        form.validateField('initial_wait_time', 'change')
      }

      const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        // prevent setting input to 0
        const numValue = e.target.value ? Number(e.target.value) : ''
        if (numValue === 0) {
          return
        }
        onChange(e)
      }

      const controls = (
        <>
          <Select
            value={selectedValue}
            onChange={(val: string | string[]) => handleSelectChange(val as string)}
            options={[
              { label: 'User speaks first', value: 'wait-for-user' },
              { label: 'Agent speaks first', value: 'speak-immediately' },
            ]}
            searchable={false}
          />
          {isEnabled && (
            <Input
              {...props}
              label="If no response, agent speaks after"
              min={1}
              max={15}
              type="number"
              value={waitTimeValue}
              onChange={handleInputChange}
              placeholder="Enter timeout in seconds"
              endIcon={<span className="text-tertiary text-p-400 mr-7">seconds</span>}
              data-testid="input-initial-wait-time"
            />
          )}
        </>
      )

      if (collapsible) {
        const summary = isEnabled
          ? `User speaks first · ${waitTimeValue || '-'}s`
          : 'Agent speaks first'
        return (
          <Collapsible>
            <CollapsibleTrigger
              className="flex w-full items-center justify-between gap-2 [&[data-state=open]>svg]:rotate-180"
              data-testid="collapsible-call-start-behavior"
            >
              <Typography variant="H4/500" className="text-foreground">
                Call Start Behavior
              </Typography>
              <span className="flex items-center gap-2">
                <Typography variant="P/400" className="text-secondary">
                  {summary}
                </Typography>
                <ChevronDown className="text-secondary size-4 transition-transform" />
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4 space-y-4">
              <Typography variant="P/400" className="text-secondary">
                Configure whether agent speaks immediately or waits for the user to greet first
              </Typography>
              {controls}
            </CollapsibleContent>
          </Collapsible>
        )
      }

      return (
        <div className="space-y-4">
          <div className="flex flex-col">
            <Typography variant="H4/500" className="text-foreground">
              Call Start Behavior
            </Typography>
            <Typography variant="P/400" className="text-secondary">
              Configure whether agent speaks immediately or waits for the user to greet first
            </Typography>
          </div>
          {controls}
        </div>
      )
    },
    eventDataExtractor: (e: { target?: { value?: string } }) =>
      e.target?.value ? Number(e.target?.value) : '',
    zodValidation,
    className: 'col-span-2 rounded-md border border-st2 p-4 space-y-4',
  } as unknown as FieldOf<T>
}

/** "Screening Contact" card — guidance + free-text call context + permission. */
export const screeningContextCard = <T,>(opts: {
  nodeVars: string[]
  contextValidation: ZodSchema
  askPermissionValidation: ZodSchema
}): FieldOf<T> =>
  ({
    name: 'screening_section',
    type: 'basic',
    component: ({ form }: { form: SharedForm }) => (
      <div className="col-span-2 rounded-md border border-st2 p-4 space-y-4">
        <div className="flex flex-col">
          <Typography variant="H4/500" className="text-foreground">
            Screening Contact
          </Typography>
          <Typography variant="P/400" className="text-secondary">
            Describe who the agent should ask for, who it is calling on behalf of, and the reason
            for the call. The agent uses this to confirm it reached the right person before
            continuing.
          </Typography>
        </div>
        <FieldsRenderer<ScreeningSharedValues>
          form={form}
          fields={[
            {
              name: 'context',
              componentType: 'textarea',
              label: 'Call context',
              zodValidation: opts.contextValidation,
              inputProps: {
                placeholder:
                  'Describe who to reach, who the call is on behalf of, and why. Use the {} button to insert per-call variables.',
                rows: 4,
                endIcon: (
                  <NodevarList
                    nodevars={opts.nodeVars}
                    inputElementSelector='textarea[name="context"]'
                  />
                ),
                'data-testid': 'textarea-screening-context',
              },
            },
            {
              name: 'ask_permission',
              componentType: 'checkbox',
              label: 'Ask permission to continue',
              zodValidation: opts.askPermissionValidation,
              inputProps: {
                helperText:
                  'Once the right person is confirmed, the agent asks whether now is a good time before moving on. Turn off to continue immediately after identity is confirmed.',
                helperTextClassName: 'text-s-400 text-secondary',
              },
            },
          ]}
        />
      </div>
    ),
  }) as unknown as FieldOf<T>

/** "On voicemail detection" card — target-node select for the Voicemail port. */
export const voicemailDetectionCard = <T,>(voicemailValidation: ZodSchema): FieldOf<T> =>
  ({
    name: 'voicemail_detection_next_node',
    type: 'basic',
    component: ({ form }: { form: SharedForm }) => (
      <div className="col-span-2 rounded-md border border-st2 p-4 space-y-4">
        <Typography variant="H4/500" className="text-foreground">
          On voicemail detection
        </Typography>
        <FieldsRenderer<ScreeningSharedValues>
          form={form}
          fields={[
            {
              name: 'voicemail_detection_next_node',
              componentType: 'select',
              zodValidation: voicemailValidation,
              inputProps: {
                options: [
                  { label: 'End Conversation', value: FlowNodeTypes.end_conversation },
                  { label: 'Play Audio', value: FlowNodeTypes.prompt },
                ],
                placeholder: 'Select next node',
                clearable: false,
              },
            },
          ]}
        />
      </div>
    ),
  }) as unknown as FieldOf<T>

// The Voicemail port is static, so the connected target is found by the port
// id itself (no intent-uuid indirection like ai_agent_v2).
const getConnectedVoicemailNode = (rf: RFInstance, nodeId: string) => {
  const edge = rf
    .getNodeConnections({ nodeId, type: 'source' })
    .find((connection) => connection.sourceHandle === SCREENING_VOICEMAIL_PORT.id)
  return edge ? rf.getNode(edge.target) : null
}

// Left on the machine when the Voicemail port routes to an End Conversation
// node. end_message is an INSTRUCTION to the closing model (added as a system
// directive at runtime, not spoken verbatim) — cx-skills production voicemail
// closes are written the same way. Generic (no company/contact name, no case
// details) and editable afterward; replaces the empty default that recorded
// dead silence. NOTE: the runtime delivers this on voicemail detection without
// waiting for a beep, so it may land over the greeting — beep-wait is a
// separate follow-up.
const DEFAULT_VOICEMAIL_END_MESSAGE =
  'Leave a brief, polite voicemail: say you called and will try to reach them again later. Keep it under 15 words and do not share any personal or case details.'

// End Conversation targets of the Voicemail port carry the generic voicemail
// message instead of an empty one; other target types (Play Audio) are left
// at their own defaults.
const voicemailTargetData = (rf: RFInstance, type: FlowNodeTypes) => {
  const data = getDefaultNodeData(type, () => rf.getNodes())
  if (type === FlowNodeTypes.end_conversation) {
    ;(data.config as { end_message?: string }).end_message = DEFAULT_VOICEMAIL_END_MESSAGE
  }
  return data
}

/** Voicemail-port canvas wiring shared by both screening panels: derive the
 * current selection from the wired node, auto-create a non-deletable End
 * Conversation when unconnected, and retype the target on save — the same
 * behavior as the first outbound ai_agent_v2 node. */
export const useVoicemailPortWiring = (selectedNode: Node) => {
  const updateNodeInternals = useUpdateNodeInternals()

  const defaultValues = useMemo(() => {
    const rf = useFlowStore.getState().flowInstance
    let voicemailDetectionValue: string = FlowNodeTypes.end_conversation

    if (rf) {
      const voicemailNode = getConnectedVoicemailNode(rf, selectedNode.id)
      if (voicemailNode) {
        voicemailDetectionValue = voicemailNode.type || FlowNodeTypes.end_conversation
      } else {
        const newNodeId = v4()
        rf.addNodes({
          id: newNodeId,
          type: FlowNodeTypes.end_conversation,
          position: { x: selectedNode.position.x - 500, y: selectedNode.position.y + 300 },
          data: voicemailTargetData(rf, FlowNodeTypes.end_conversation),
          deletable: false,
        })
        rf.addEdges({
          id: v4(),
          source: selectedNode.id,
          target: newNodeId,
          sourceHandle: SCREENING_VOICEMAIL_PORT.id,
          deletable: false,
        })
      }
    }

    return { voicemail_detection_next_node: voicemailDetectionValue }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode.id])

  const handleFormSave = <T extends { voicemail_detection_next_node?: string }>(data: T): T => {
    const rf = useFlowStore.getState().flowInstance
    if (!rf) return data

    const effectiveNodeType = (data.voicemail_detection_next_node ||
      FlowNodeTypes.end_conversation) as FlowNodeTypes
    const voicemailNode = getConnectedVoicemailNode(rf, selectedNode.id)

    if (voicemailNode && voicemailNode.type !== effectiveNodeType) {
      rf.updateNode(voicemailNode.id, {
        type: effectiveNodeType,
        data: voicemailTargetData(rf, effectiveNodeType),
      })
    } else if (!voicemailNode) {
      const newNodeId = v4()
      rf.addNodes({
        id: newNodeId,
        type: effectiveNodeType,
        position: { x: selectedNode.position.x - 500, y: selectedNode.position.y + 300 },
        data: voicemailTargetData(rf, effectiveNodeType),
        deletable: false,
      })
      rf.addEdges({
        id: v4(),
        source: selectedNode.id,
        target: newNodeId,
        sourceHandle: SCREENING_VOICEMAIL_PORT.id,
        deletable: false,
      })
    }

    updateNodeInternals(selectedNode.id)
    return data
  }

  return { defaultValues, handleFormSave }
}
